import { useTranslation } from 'react-i18next';
import React from 'react';
import { Alert, Box, Card, CardContent, Typography } from '@mui/material';
import TransactionList from './TransactionList';
import Link from '@mui/material/Link';
import { ArrowForward } from '@mui/icons-material';
import LoadingIndicator from '../../../components/common/LoadingIndicator';
import Transaction from '../../../services/transaction/Transaction';
import TransactionType from '../../../services/transaction/TransactionType';
import { Status } from '../../../common/hooks/useApi';
import RouterLink from 'common/hooks/RouterLink';

const MAX_RECENT = 7;

interface RecentTransactionListProps {
  transactions: Transaction[] | undefined;
  fetchingTransactionStatus: Status;
  transactionTypes: TransactionType[] | undefined;
  fetchingTransTypeStatus: Status;
}

const RecentTransactions = ({
  transactions,
  fetchingTransactionStatus,
  transactionTypes,
  fetchingTransTypeStatus,
}: RecentTransactionListProps): JSX.Element => {
  const { t, i18n } = useTranslation('familiplusPage');
  const isDataPending = fetchingTransactionStatus === Status.Pending || fetchingTransTypeStatus === Status.Pending;
  const isDataResolved = fetchingTransactionStatus === Status.Resolved && fetchingTransTypeStatus === Status.Resolved;
  const hasFetchDataFailed =
    fetchingTransactionStatus === Status.Rejected || fetchingTransTypeStatus === Status.Rejected;

  return (
    <Card id="RecentTransactions">
      <CardContent>
        <Typography variant="h3" component="h2" sx={{ mb: '12px' }}>
          {t('transactions.recentTitle')}
        </Typography>

        {isDataPending && <LoadingIndicator />}
        {hasFetchDataFailed && (
          <Alert id="fetchDataFailed" severity="error">
            {t('transactions.fetchDataFailed')}
          </Alert>
        )}
        {isDataResolved && (
          <>
            {transactions && transactions.length > 0 ? (
              <>
                <Box sx={{ mx: '-16px', mb: '8px' }}>
                  <TransactionList
                    transactions={transactions.slice(0, MAX_RECENT)}
                    transactionTypes={transactionTypes || []}
                  />
                </Box>
                <Link
                  id="transactionLink"
                  component={RouterLink}
                  to={`/${i18n.resolvedLanguage}/${
                    i18n.resolvedLanguage == 'fr' ? 'Compte' : 'Account'
                  }/Familiplus/transactions`}
                  className="iconTextLink">
                  {t('transactions.seeAll')}
                  <ArrowForward sx={{ ml: '6px' }} fontSize="small" />
                </Link>
              </>
            ) : (
              <Box data-cy="NoResults" sx={{ fontStyle: 'italic', fontSize: '0.875rem' }}>
                {t('transactions.noResult')}
              </Box>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default RecentTransactions;
